import { useCallback, useState } from 'react';
import { IndexedStep, Step } from 'components/Wizard';
import { capitalize } from 'utils/string';
import { UseVideoSubFlow, VideoFlowStep } from '../types';
import useAspectRatioStep from '../useAspectRatioStep';
import useClipVideoStep from '../useClipVideoStep';
import useUploadVideoStep from '../useUploadVideoStep';
import { isNavigatingToEarlierStep } from '../utils';

const useSingleClipSubFlow: UseVideoSubFlow = ({
  onError,
  rootStepId,
  state,
  stateDispatch,
  wizardRef,
}) => {
  const [aspectRatioSelected, setAspectRatioSelected] = useState(false);

  const { aspectRatioName } = state;

  const { step: aspectRatioStep } = useAspectRatioStep({
    aspectRatioName,
    onSubmit: newAspectRatioName => {
      stateDispatch({
        type: 'aspect-ratio-select',
        payload: { aspectRatioName: newAspectRatioName },
      });
      setAspectRatioSelected(true);
    },
    onBackClick: () => {
      wizardRef.current?.jump(rootStepId);
    },
  });

  const {
    cleanup: cleanupVideoUpload,
    step: uploadVideoStep,
    reset,
  } = useUploadVideoStep({
    objective: 'SingleClip',
    onError,
    state,
    stateDispatch,
    wizardRef,
  });

  const {
    bodyClassName,
    buttonClassName,
    step: clipVideoStep,
  } = useClipVideoStep({
    state,
    stateDispatch,
    wizardRef,
  });

  const handleStepChange = useCallback(
    (toStep: IndexedStep, _fromStep, _action, steps: Step<VideoFlowStep>[]) => {
      if (
        isNavigatingToEarlierStep(VideoFlowStep.UPLOAD_VIDEO, toStep, steps)
      ) {
        reset();
      }
    },
    [reset],
  );

  const cleanup = useCallback(() => {
    cleanupVideoUpload();
  }, [cleanupVideoUpload]);

  const handleStepClick = useCallback(
    async (toStep: IndexedStep<VideoFlowStep>) => {
      if (toStep.stepId === rootStepId) {
        wizardRef.current?.jump(VideoFlowStep.ASPECT_RATIO);

        // returning false here stops the wizard from navigating to the objective
        // step so that we can override this and send the user to the aspect ratio step
        return false;
      }
      return true;
    },
    [rootStepId, wizardRef],
  );

  if (state.isAspectRatioLocked) {
    return {
      bodyClassName,
      buttonClassName,
      cleanup,
      steps: [uploadVideoStep, clipVideoStep],
      onStepChange: handleStepChange,
    };
  }

  return {
    bodyClassName,
    buttonClassName,
    cleanup,
    rootStepCompletedName: aspectRatioSelected
      ? capitalize(state.aspectRatioName)
      : undefined,
    steps: [aspectRatioStep, uploadVideoStep, clipVideoStep],
    onStepChange: handleStepChange,
    onStepClick: handleStepClick,
  };
};

export default useSingleClipSubFlow;
